import { BigNumber } from 'bignumber.js';

const PERCENT_MULTIPLIER = 100;
const SECONDS_PER_MINUTE = 60;

// numberToString precision tuning
const NO_DECIMAL_LIMIT = 1000; // sentinel when no exact representation is found within the probe limit
const EXACT_DECIMALS_PROBE_LIMIT = 60; // max decimals probed to detect an exact representation
const MAX_FRACTION_DIGITS = 100; // hard cap on formatted decimals
const DECIMAL_BASE = 10;
const SMALL_VALUE_DECIMALS = 4; // decimals for values in [0.01, 1)
const TINY_VALUE_DECIMALS = 8; // decimals fallback for the smallest values
const MAX_SMALL_VALUE_DECIMALS = 17; // upper bound when scaling decimals for sub-0.01 values

// magnitude thresholds / divisors
const HUNDREDTH = 0.01;
const HUNDRED = 100;
const THOUSAND = 1000;
const TEN_THOUSAND = 10_000;
const HUNDRED_THOUSAND = 100_000;
const MILLION = 1_000_000;
const HUNDRED_MILLION = 100_000_000;

export const percentToString = (p: number, fractions = 0): string =>
  (p * PERCENT_MULTIPLIER).toFixed(fractions);
export const secondsToString = (s: number): string => {
  const seconds = (s % SECONDS_PER_MINUTE).toString().padStart(2, '0');
  const minutes = parseInt((s / SECONDS_PER_MINUTE).toString())
    .toString()
    .padStart(2, '0');
  return `${minutes}:${seconds}`;
};

export const numberToString = (
  number: BigNumber | string | null,
  minDecimals: number | null = null,
  maxDecimals: number | null = null,
): string => {
  if (number === null) {
    return '';
  }
  if (number === '') {
    return '';
  }

  const n = new BigNumber(removeSeparator(number));
  const roundingMode = 1;
  let maxI = NO_DECIMAL_LIMIT;
  for (let i = 0; i < EXACT_DECIMALS_PROBE_LIMIT; i++) {
    if (new BigNumber(n.toFixed(i, roundingMode)).eq(n)) {
      maxI = i;
      break;
    }
  }

  if (n.gte(TEN_THOUSAND)) {
    return n.toFormat(0, roundingMode);
  }
  if (n.gte(THOUSAND)) {
    return n.toFormat(
      Math.min(maxI, Math.min(maxDecimals || MAX_FRACTION_DIGITS, Math.max(minDecimals || 0, 1))),
      roundingMode,
    );
  }
  if (n.gte(HUNDRED)) {
    return n.toFormat(
      Math.min(maxI, Math.min(maxDecimals || MAX_FRACTION_DIGITS, Math.max(minDecimals || 0, 1))),
      roundingMode,
    );
  }
  if (n.gte(1)) {
    return n.toFormat(
      Math.min(maxI, Math.min(maxDecimals || MAX_FRACTION_DIGITS, Math.max(minDecimals || 0, 2))),
      roundingMode,
    );
  }
  if (n.gte(HUNDREDTH)) {
    return n.toFormat(
      Math.min(
        maxI,
        Math.min(
          maxDecimals || MAX_FRACTION_DIGITS,
          Math.max(minDecimals || 0, SMALL_VALUE_DECIMALS),
        ),
      ),
      roundingMode,
    );
  }
  for (let i = minDecimals || SMALL_VALUE_DECIMALS; i < MAX_SMALL_VALUE_DECIMALS; i++) {
    if (n.gte(Math.pow(DECIMAL_BASE, -i))) {
      return n.toFormat(
        Math.min(maxI, Math.min(maxDecimals || MAX_FRACTION_DIGITS, Math.max(minDecimals || 0, i))),
        roundingMode,
      );
    }
  }
  if (n.isEqualTo(0)) {
    return '0';
  }

  return n.toFormat(
    Math.min(
      maxI,
      Math.min(maxDecimals || MAX_FRACTION_DIGITS, Math.max(minDecimals || 0, TINY_VALUE_DECIMALS)),
    ),
    roundingMode,
  );
};

/** Precise USD currency string, e.g. `$1,234.56`. For the abbreviated form use `dollarToConciseString`. */
export function formatUsd(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function dollarToConciseString(num: number | undefined): string {
  if (!num) {
    return '-';
  }
  if (num < 1) {
    return ' < 1$';
  }
  if (num < THOUSAND) {
    return numberToString(new BigNumber(num)) + '$';
  }
  if (num < TEN_THOUSAND) {
    return parseInt((num / HUNDRED).toString()) / DECIMAL_BASE + 'K';
  }
  if (num < MILLION) {
    return parseInt((num / THOUSAND).toString()) + 'K';
  }
  if (num < HUNDRED_MILLION) {
    return parseInt((num / HUNDRED_THOUSAND).toString()) / DECIMAL_BASE + 'M';
  }
  return parseInt((num / MILLION).toString()) + 'M';
}

export function removeSeparator(number: BigNumber | string | number) {
  return typeof number === 'string' ? number.replace(/,/g, '') : number;
}
